import { useRef, useState } from "react";
import { IconPhoto, IconTrash, IconUpload, IconAlertTriangle, IconPlus } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";
import { Button } from "../../../../components/common/button";
import { SectionCard } from "../../../../components/common/section-card";
import { Select } from "../../../../components/forms/select";
import { Dialog } from "../../../../components/common/dialog";
import { resolveImageSource } from "../../../../components/common/image";
import type { ProductAttributeLineDto } from "../../../../api";

export interface EditVariantRow {
  id: number;
  variant: string;
  sku: string;
  price: string;
  stock: string;
  discount: string;
  imageUrl?: string;
  /** Set when the seller picked a new file in this session, pending upload. */
  imageFile?: File;
}

/** One in-progress new variant the seller is composing. Keyed by `draftId`
 *  client-side; on submit it gets converted to a NewVariantDto. */
export interface NewVariantDraft {
  draftId: string;
  /** Per attributeId: either an existing valueId (number) or a typed name (string). */
  axisChoices: Record<number, { valueId?: number; newValueName?: string }>;
  sku: string;
  price: string;
  stock: string;
  discount: string;
  imageUrl?: string;
  imageFile?: File;
}

interface Props {
  variants: EditVariantRow[];
  onVariantUpdate: (id: number, field: keyof EditVariantRow, value: string) => void;
  onVariantImageChange: (id: number, file: File, previewUrl: string) => void;
  onVariantRemove: (id: number) => void;
  /** Attribute axes available on the product. Empty array = "add variant" disabled. */
  attributeLines: ProductAttributeLineDto[];
  newVariants: NewVariantDraft[];
  onNewVariantAdd: () => void;
  onNewVariantUpdate: (draftId: string, patch: Partial<NewVariantDraft>) => void;
  onNewVariantAxisChange: (
    draftId: string,
    attributeId: number,
    choice: { valueId?: number; newValueName?: string },
  ) => void;
  onNewVariantImageChange: (draftId: string, file: File, previewUrl: string) => void;
  onNewVariantRemove: (draftId: string) => void;
}

const NEW_VALUE_SENTINEL = "__new__";

function VariantImagePicker({
  imageUrl,
  alt,
  onPick,
}: {
  imageUrl?: string;
  alt: string;
  onPick: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      aria-label={imageUrl ? "Replace variant image" : "Upload variant image"}
      className="group relative flex size-12 items-center justify-center overflow-hidden rounded-xl border border-borderDefault bg-bgLight hover:border-brandPrimary"
    >
      {imageUrl ? (
        <img src={resolveImageSource(imageUrl) ?? undefined} alt={alt} className="size-full object-cover" />
      ) : (
        <IconPhoto size={20} className="text-textMuted" />
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-textDark/50 opacity-0 transition-opacity group-hover:opacity-100">
        <IconUpload size={16} className="text-bgWhite" />
      </span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          e.target.value = "";
        }}
      />
    </button>
  );
}

export function EditVariantsSection({
  variants,
  onVariantUpdate,
  onVariantImageChange,
  onVariantRemove,
  attributeLines,
  newVariants,
  onNewVariantAdd,
  onNewVariantUpdate,
  onNewVariantAxisChange,
  onNewVariantImageChange,
  onNewVariantRemove,
}: Props) {
  const [removeTarget, setRemoveTarget] = useState<EditVariantRow | null>(null);
  const canAddVariant = attributeLines.length > 0;

  return (
    <>
      <SectionCard shadow title="Variants & pricing" className="gap-5">
        <Text variant="bodySm" tone="dark">
          {variants.length} variant{variants.length === 1 ? "" : "s"} on this product
          {newVariants.length > 0 ? ` (+ ${newVariants.length} pending)` : ""}
        </Text>

        <div className="overflow-hidden rounded-xl border border-borderDefault">
          {/* Phones: keep the columns readable and scroll the table sideways. */}
          <div className="overflow-x-auto">
          <div className="max-md:min-w-[680px]">
          <div className="grid grid-cols-[56px_minmax(0,2fr)_repeat(4,minmax(0,1fr))_40px] gap-4 bg-bgLight px-6 py-3">
            {["Image", "Variant", "SKU", "Price (QAR)", "Stock", "Discount %", ""].map((h) => (
              <Text key={h} variant="capsBig" tone="dark">{h}</Text>
            ))}
          </div>
          {variants.map((v) => (
            <div
              key={v.id}
              className="grid grid-cols-[56px_minmax(0,2fr)_repeat(4,minmax(0,1fr))_40px] items-center gap-4 border-t border-borderDefault px-6 py-4"
            >
              <VariantImagePicker
                imageUrl={v.imageUrl}
                alt={v.variant}
                onPick={(file) =>
                  onVariantImageChange(v.id, file, URL.createObjectURL(file))
                }
              />

              <span className="min-w-0 truncate" title={v.variant}>
                <Text variant="bodyBold" tone="dark" inline>{v.variant}</Text>
              </span>
              <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                <input
                  value={v.sku}
                  readOnly
                  className="w-full border-0 bg-transparent text-bodySm text-textMuted outline-none"
                />
              </div>
              <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                <input
                  value={v.price}
                  onChange={(e) => onVariantUpdate(v.id, "price", e.target.value)}
                  className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none"
                />
              </div>
              <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                <input
                  value={v.stock}
                  onChange={(e) => onVariantUpdate(v.id, "stock", e.target.value)}
                  className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none"
                />
              </div>
              <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                <input
                  value={v.discount}
                  onChange={(e) => onVariantUpdate(v.id, "discount", e.target.value)}
                  placeholder="0"
                  className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                />
              </div>
              <IconPill
                as="button"
                size={24}
                tone="ghost"
                aria-label="Remove variant"
                onClick={() => setRemoveTarget(v)}
                className="!bg-transparent !text-textDark transition-all hover:[&>svg]:stroke-[2.5]"
              >
                <IconTrash size={16} />
              </IconPill>
            </div>
          ))}
          </div>
          </div>
        </div>

        {/* New (pending) variants — rendered as standalone cards OUTSIDE the
            overflow-hidden table wrapper above, so the axis dropdowns aren't
            clipped. Fields are laid out in a responsive grid; the Select menu
            scrolls internally when the value list is long. */}
        {newVariants.map((draft) => (
          <div
            key={draft.draftId}
            className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-accentYellowTint/10 p-4 sm:p-5"
          >
            <div className="flex items-center justify-between gap-3">
              <Text variant="bodyXsBold" tone="muted" className="uppercase tracking-wide">
                New variant
              </Text>
              <IconPill
                as="button"
                size={24}
                tone="ghost"
                aria-label="Remove pending variant"
                onClick={() => onNewVariantRemove(draft.draftId)}
                className="!bg-transparent !text-textDark transition-all hover:[&>svg]:stroke-[2.5]"
              >
                <IconTrash size={16} />
              </IconPill>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <VariantImagePicker
                imageUrl={draft.imageUrl}
                alt="New variant"
                onPick={(file) =>
                  onNewVariantImageChange(draft.draftId, file, URL.createObjectURL(file))
                }
              />

              <div className="flex min-w-0 flex-1 flex-col gap-4">
                {/* Attribute axes */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {attributeLines.map((line) => {
                    const choice = draft.axisChoices[line.attributeId];
                    const selectValue =
                      choice?.valueId != null
                        ? String(choice.valueId)
                        : choice?.newValueName !== undefined
                          ? NEW_VALUE_SENTINEL
                          : "";
                    return (
                      <div key={line.attributeId} className="flex flex-col gap-1">
                        <Text variant="bodyXs" tone="muted">{line.attributeName}</Text>
                        <Select
                          name={`new-variant-${draft.draftId}-axis-${line.attributeId}`}
                          density="compact"
                          placeholder="— pick a value —"
                          value={selectValue}
                          options={[
                            ...line.values.map((val) => ({
                              value: String(val.id),
                              label: val.name,
                            })),
                            { value: NEW_VALUE_SENTINEL, label: "Type new…" },
                          ]}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === NEW_VALUE_SENTINEL) {
                              onNewVariantAxisChange(draft.draftId, line.attributeId, {
                                newValueName: "",
                              });
                            } else if (v === "") {
                              onNewVariantAxisChange(draft.draftId, line.attributeId, {});
                            } else {
                              onNewVariantAxisChange(draft.draftId, line.attributeId, {
                                valueId: Number(v),
                              });
                            }
                          }}
                        />
                        {choice?.newValueName !== undefined && (
                          <input
                            autoFocus
                            placeholder={`New ${line.attributeName.toLowerCase()} value`}
                            value={choice.newValueName}
                            onChange={(e) =>
                              onNewVariantAxisChange(draft.draftId, line.attributeId, {
                                newValueName: e.target.value,
                              })
                            }
                            className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 text-bodySm outline-none transition-colors focus:border-brandPrimary"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* SKU / price / stock / discount */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="flex flex-col gap-1">
                    <Text variant="bodyXs" tone="muted">SKU</Text>
                    <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                      <input
                        value={draft.sku}
                        onChange={(e) => onNewVariantUpdate(draft.draftId, { sku: e.target.value })}
                        placeholder="SKU"
                        className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text variant="bodyXs" tone="muted">Price (QAR)</Text>
                    <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                      <input
                        type="number"
                        value={draft.price}
                        onChange={(e) => onNewVariantUpdate(draft.draftId, { price: e.target.value })}
                        placeholder="Price"
                        className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text variant="bodyXs" tone="muted">Stock</Text>
                    <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                      <input
                        type="number"
                        value={draft.stock}
                        onChange={(e) => onNewVariantUpdate(draft.draftId, { stock: e.target.value })}
                        placeholder="Stock"
                        className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text variant="bodyXs" tone="muted">Discount %</Text>
                    <div className="rounded-xl border border-borderDefault bg-bgWhite px-3 py-2 transition-colors focus-within:border-brandPrimary">
                      <input
                        type="number"
                        value={draft.discount}
                        onChange={(e) => onNewVariantUpdate(draft.draftId, { discount: e.target.value })}
                        placeholder="0"
                        className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div>
          <Button
            variant="secondaryDark"
            size="sm"
            leftIcon={<IconPlus size={14} />}
            onClick={onNewVariantAdd}
            disabled={!canAddVariant}
            title={
              canAddVariant
                ? undefined
                : "This product has no attribute axes; can't add a variant."
            }
            className="self-start"
          >
            Add variant
          </Button>
        </div>
      </SectionCard>

      <Dialog
        open={removeTarget !== null}
        setOpen={(open) => { if (!open) setRemoveTarget(null); }}
        titleIcon={<IconAlertTriangle size={24} className="text-accentError" />}
        title="Remove variant?"
        titleAlignment="left"
        text={`Are you sure you want to remove "${removeTarget?.variant ?? "this variant"}"? This action cannot be undone once you save your changes.`}
        buttonText="Remove"
        cancelText="Keep variant"
        onConfirm={() => {
          if (removeTarget) onVariantRemove(removeTarget.id);
          setRemoveTarget(null);
        }}
      />
    </>
  );
}
